import { useEffect, useState } from "react";
import { flags, getLocaleFromPath, homePath, privacyPath, translations } from "@/i18n";
import { Clock, Coins, Facebook, Instagram, MapPin, Phone, Tag } from "lucide-react";
import { Hero } from "@/components/site/Hero";
import { BrandMarquee } from "@/components/site/BrandMarquee";
import { BRAND_LOGOS } from "@/lib/brands";
import "@/App.css";


function App() {
  const locale = getLocaleFromPath(window.location.pathname);
  const t = translations[locale];

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = t.meta.title;
    const updateMeta = (selector, value) => {
      const element = document.querySelector(selector);
      if (element) element.setAttribute("content", value);
    };
    updateMeta('meta[name="description"]', t.meta.description);
    updateMeta('meta[property="og:title"]', t.meta.ogTitle);
    updateMeta('meta[property="og:description"]', t.meta.ogDescription);
    updateMeta('meta[property="og:locale"]', t.meta.ogLocale);
  }, [locale, t]);

  const [contactStatus, setContactStatus] = useState("");

  const [contactSending, setContactSending] = useState(false);

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    if (contactSending) return;
    const form = event.currentTarget;
    const fields = Object.fromEntries(new FormData(form).entries());

    setContactStatus(t.contact.sending);
    setContactSending(true);

    try {
      const response = await fetch("https://forms.elido.nu/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_id: "outlet-bastad",
          subject: "Fråga till Outlet Båstad",
          name: fields.name,
          email: fields.email,
          phone: fields.phone || "",
          message: fields.message,
          website: fields.website || "",
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.ok || !result?.emailed) {
        throw new Error("E-postleverans kunde inte bekräftas.");
      }
      form.reset();
      setContactStatus(t.contact.success);
    } catch (_error) {
      setContactStatus(t.contact.error);
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div className="site-shell" lang={locale}>
      <header className="topbar">
        <a className="brand" href="#hem" aria-label={`Outlet Båstad – ${t.nav.home}`}>
          <img src="/assets/logo-outlet-bastad.svg" alt="Outlet Båstad" />
        </a>
        <nav className="nav" aria-label={t.nav.main}>
          <a href="#hem">{t.nav.home}</a>
          <a href="#varumarken">{t.nav.brands}</a>
          <a href="#butiken">{t.nav.about}</a>
          <a href="#hitta">{t.nav.find}</a>
        </nav>
        <div className="nav-actions">
          <div className="header-social" aria-label={t.social.follow}>
            <a href="https://www.facebook.com/markesportalen/?locale=sv_SE" target="_blank" rel="noopener noreferrer" aria-label={`${t.social.follow}: Facebook`}><Facebook aria-hidden="true" /></a>
            <a href="https://www.instagram.com/outletbastad/" target="_blank" rel="noopener noreferrer" aria-label={`${t.social.follow}: Instagram`}><Instagram aria-hidden="true" /></a>
          </div>
          <details className="language-switcher">
            <summary aria-label={t.language.choose} title={t.language.choose}>
              <span className="language-flag" aria-hidden="true">{flags[locale]}</span>
              <span className="language-caret" aria-hidden="true">⌄</span>
            </summary>
            <div className="language-options" role="group" aria-label={t.language.choose}>
              {["sv", "da", "en"].map((language) => (
                <a key={language} href={homePath(language)} lang={language} aria-current={locale === language ? "page" : undefined}>
                  <span className="language-flag" aria-hidden="true">{flags[language]}</span>
                  <span>{t.language[language]}</span>
                </a>
              ))}
            </div>
          </details>
          <a className="nav-cta" href="#kontakt">{t.nav.contact}</a>
        </div>
      </header>

      <main>
        <Hero t={t} />
        <section className="brands brands-feature section-pad" id="varumarken">
          <div className="tennis-court-mark" aria-hidden="true" />
          <div className="section-head brands-head">
            <div>
              <p className="section-label">{t.brands.label}</p>
              <h2>{t.brands.title}</h2>
            </div>
            <p className="brands-subcopy">{t.brands.note}</p>
          </div>

          <div className="brand-marquee-wrap">
            <BrandMarquee items={BRAND_LOGOS.slice(0, 12)} label={t.brands.aria} />
            <BrandMarquee items={BRAND_LOGOS.slice(12)} reverse label={t.brands.aria} />
          </div>

        </section>

        <section className="stop-strip" aria-labelledby="stop-strip-title">
          <img className="stop-strip-photo" src="/assets/butik-interior.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" />
          <div className="stop-strip-shade" aria-hidden="true" />
          <div className="stop-strip-inner">
            <div className="stop-strip-title">
              <p className="section-label">Outlet Båstad</p>
              <h2 id="stop-strip-title">{t.stop.title}</h2>
            </div>

            <div className="stop-strip-points">
              <div className="stop-point">
                <span className="stop-icon"><Tag aria-hidden="true" /></span>
                <div>
                  <h3>{t.stop.brandTitle}</h3>
                  <p>{t.stop.brandCopy}</p>
                </div>
              </div>

              <div className="stop-point">
                <span className="stop-icon"><Coins aria-hidden="true" /></span>
                <div>
                  <h3>{t.stop.priceTitle}</h3>
                  <p>{t.stop.priceCopy}<strong className="stop-point-discount">{t.hero.discountRange} {t.hero.discountLabel}</strong></p>
                </div>
              </div>

              <div className="stop-point">
                <span className="stop-icon"><MapPin aria-hidden="true" /></span>
                <div>
                  <h3>{t.stop.locationTitle}</h3>
                  <p>{t.stop.locationCopy}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="intro section-pad" id="butiken">
          <div className="intro-copy">
            <p className="section-label">Outlet Båstad</p>
            <h2>{t.intro.title}</h2>
            <p>{t.intro.first}</p>
            <p>{t.intro.second}</p>
            <figure className="intro-evening">
              <img
                src="/assets/butik-fasad-kvall.avif"
                alt={t.intro.eveningAlt}
                width="800"
                height="450"
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
          <div className="intro-image">
            <img src="/assets/butik-interior.webp" alt={t.intro.imageAlt} loading="lazy" decoding="async" />
          </div>
        </section>

        <section className="visit" id="hitta">
          <div className="visit-info-band">
            <svg className="visit-ridge" viewBox="0 0 1200 120" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0 93 C105 86 150 64 236 73 C323 82 360 42 452 55 C544 68 584 32 675 46 C765 60 817 25 913 42 C1009 59 1061 39 1200 49" />
            </svg>
            <div className="visit-info-inner">
              <div className="visit-info-item">
                <MapPin aria-hidden="true" />
                <div>
                  <span>{t.visit.address}</span>
                  <strong>Tomtaholmsvägen 1<br />269 41 Östra Karup</strong>
                  <p className="visit-landmark">{t.visit.landmark}</p>
                  <a href="https://www.google.com/maps/search/?api=1&query=Tomtaholmsv%C3%A4gen%201%2C%20269%2041%20%C3%96stra%20Karup" target="_blank" rel="noreferrer">{t.visit.openMap}</a>
                </div>
              </div>
              <div className="visit-info-item">
                <Clock aria-hidden="true" />
                <div>
                  <span>{t.visit.hours}</span>
                  <strong>{t.visit.weekday}<br />{t.visit.saturday}<br />{t.visit.sunday}</strong>
                </div>
              </div>
              <div className="visit-info-item">
                <Phone aria-hidden="true" />
                <div>
                  <span>{t.visit.contact}</span>
                  <strong><a href="tel:043170045">0431-700 45</a></strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="contact-form-section" id="kontakt">
          <div className="contact-form-inner">
            <div className="contact-form-copy">
              <p className="contact-kicker">{t.contact.label}</p>
              <h2>{t.contact.title}</h2>
              <p className="contact-intro">
                {t.contact.intro}
              </p>
              <a className="contact-phone" href="tel:043170045">
                <Phone aria-hidden="true" />
                <span>
                  <small>{t.contact.call}</small>
                  <strong>0431-700 45</strong>
                </span>
              </a>
            </div>

            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="contact-form-row">
                <label>
                  <span>{t.contact.name}</span>
                  <input type="text" name="name" autoComplete="name" required />
                </label>
                <label>
                  <span>{t.contact.email}</span>
                  <input type="email" name="email" autoComplete="email" required />
                </label>
              </div>

              <label>
                <span>{t.contact.phone} <em>{t.contact.optional}</em></span>
                <input type="tel" name="phone" autoComplete="tel" />
              </label>

              <label>
                <span>{t.contact.message}</span>
                <textarea name="message" rows="5" required />
              </label>

              <div className="contact-honeypot" aria-hidden="true">
                <label htmlFor="contact-website">{t.contact.honeypot}</label>
                <input id="contact-website" type="text" name="website" tabIndex={-1} autoComplete="off" />
              </div>
              <p className="contact-privacy">
                {t.contact.privacyStart}
                <a href={privacyPath(locale)}>{t.contact.privacyLink}</a>{t.contact.privacyEnd}
              </p>
              <div className="contact-form-actions">
                <button type="submit" disabled={contactSending}>
                  {contactSending ? t.contact.sending : t.contact.submit}
                </button>
                <p role="status" aria-live="polite">{contactStatus}</p>
              </div>
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <img src="/assets/logo-outlet-bastad.svg" alt="Outlet Båstad" />
        <div className="footer-meta">
          <div className="footer-details">
            <a className="footer-address" href="https://www.google.com/maps/search/?api=1&query=Tomtaholmsv%C3%A4gen%201%2C%20269%2041%20%C3%96stra%20Karup" target="_blank" rel="noopener noreferrer">
              <span className="footer-mobile-label">{t.footer.visit}</span>
              <span className="footer-street">Tomtaholmsvägen 1</span>
              <span className="footer-separator" aria-hidden="true"> · </span>
              <span className="footer-city">269 41 Östra Karup</span>
            </a>
            <span className="footer-separator" aria-hidden="true"> · </span>
            <a className="footer-phone" href="tel:+4643170045">
              <span className="footer-mobile-label">{t.footer.call}</span>
              <span>0431-700 45</span>
            </a>
          </div>
          <span className="game-set-shop" aria-hidden="true">GAME · SET · SHOP.</span>
          <a className="footer-privacy" href={privacyPath(locale)}>{t.footer.privacy}</a>
        </div>
        <div className="footer-social" aria-label={t.social.follow}>
          <a href="https://www.facebook.com/markesportalen/?locale=sv_SE" target="_blank" rel="noopener noreferrer"><Facebook aria-hidden="true" />Facebook</a>
          <a href="https://www.instagram.com/outletbastad/" target="_blank" rel="noopener noreferrer"><Instagram aria-hidden="true" />Instagram</a>
        </div>
        <div className="footer-credit">
          <span>{t.footer.credit}</span>
          <a href="https://elido.nu/" target="_blank" rel="noopener noreferrer">Elido</a>
        </div>
      </footer>
    </div>
  );
}

export default App;

